import { resolveStrapiUrl } from '@utils/library/resolveStrapiUrl';
import classNames from 'classnames';
import Image from 'next/image';
import React, { JSX, useCallback, useId, useRef, useState } from 'react';

import { Tooltip } from '@components/library/atoms/Tooltip';
import { FavoriteToggle } from '@components/library/molecules/FavoriteToggle';
import { ObjectHoverCard } from '@components/library/molecules/ObjectHoverCard';
import { SelectToggle } from '@components/library/molecules/SelectToggle';

import type { BookCardProps } from './BookCard.types';

import styles from './BookCard.module.scss';

// motion-passport: exempt — this file carries no animation of its own. The
// card's hover lift lives in BookCard.module.scss and the dossier's fade in
// ObjectHoverCard.module.scss; both stylesheets hold the reduced-motion branch.

export function BookCard({
  object,
  onClick,
  className,
  selected = false,
  onSelectToggle,
  selectDisabled = false,
  selectReason,
  compact = false,
  showHoverCard = !compact,
  ownerUsername,
  favorite = false,
  onFavoriteToggle,
  favoriteBusy = false,
  hidden = false,
}: BookCardProps): JSX.Element {
  const { attributes } = object;
  const coverUrl = resolveStrapiUrl(
    attributes.coverImage?.data?.attributes.url,
  );
  const tags = attributes.tags?.data ?? [];
  const title = attributes.title;

  // The cover fades in over the mockup once it decodes instead of popping. A
  // cached image can finish before React attaches `onLoad`, so the ref's
  // `complete` flag catches that case.
  const [coverLoaded, setCoverLoaded] = useState(false);
  const coverRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete) setCoverLoaded(true);
  }, []);

  // Hovering (or tabbing to) the book opens its dossier beside the shelf.
  const cardRef = useRef<HTMLDivElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // The dossier is announced as this card's description while it is open.
  // Per instance, not per object: a starred book stands on its own shelf and
  // on the Favorites shelf at once, and two elements cannot share an id.
  const dossierId = `object-dossier-${object.id}-${useId()}`;

  const handleActivate = () => {
    // Opening the overview covers the card, so the dossier steps aside first.
    setPreviewOpen(false);
    onClick?.(object);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  return (
    <div
      className={classNames(styles.row, className, {
        [styles.compact]: compact,
      })}
    >
      <div
        ref={cardRef}
        className={classNames(styles.card, { [styles.selected]: selected })}
        role="button"
        tabIndex={0}
        aria-label={
          selected ? `Open ${title} (in share selection)` : `Open ${title}`
        }
        aria-describedby={dossierId}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
        onMouseEnter={() => setPreviewOpen(true)}
        onMouseLeave={() => setPreviewOpen(false)}
        // A drag captures the pointer, so no mouseleave arrives to close the
        // dossier while the card travels; the press closes it instead.
        onPointerDown={() => setPreviewOpen(false)}
        // Only a keyboard focus opens the dossier. Focus also lands here when a
        // modal closes and hands it back, with the pointer nowhere near, and
        // the panel then stood open until something else was hovered.
        onFocus={e => {
          if (e.currentTarget.matches(':focus-visible')) setPreviewOpen(true);
        }}
        onBlur={() => setPreviewOpen(false)}
      >
        {onSelectToggle && (
          <div className={styles.select}>
            <SelectToggle
              selected={selected}
              onToggle={onSelectToggle}
              disabled={selectDisabled && !selected}
              reason={selectReason}
            />
          </div>
        )}
        <div className={styles.placeholder} aria-hidden="true" />
        <div className={styles.cover}>
          {coverUrl && (
            <Image
              ref={coverRef}
              src={coverUrl}
              alt={attributes.title}
              fill
              sizes="146px"
              className={classNames(styles.coverImage, {
                [styles.coverImageLoaded]: coverLoaded,
              })}
              onLoad={() => setCoverLoaded(true)}
            />
          )}
          {/* A veil over the art, and the word for what it means. Said on the
              cover rather than beside the title: the cover is what a visitor
              would be looking at, and it is the thing they do not get. */}
          {hidden && (
            <span className={styles.hiddenVeil}>
              <span className={styles.hiddenLabel}>Hidden</span>
            </span>
          )}
        </div>
        {(favorite || onFavoriteToggle) && !compact && (
          <div
            className={classNames(styles.favorite, {
              [styles.favoriteOn]: favorite,
            })}
          >
            <FavoriteToggle
              favorite={favorite}
              onToggle={onFavoriteToggle}
              busy={favoriteBusy}
              title={title}
            />
          </div>
        )}
      </div>

      {/* Always render the tag column (even when empty) so the card keeps a
          consistent width whether or not the object has tags. */}
      <div className={styles.tags} aria-label="Tags">
        {tags.map(tag => (
          <Tooltip asChild tooltipContent={tag.attributes.name} key={tag.id}>
            <span
              key={tag.id}
              className={styles.tagDot}
              style={{ backgroundColor: tag.attributes.color }}
            />
          </Tooltip>
        ))}
      </div>

      <ObjectHoverCard
        id={dossierId}
        object={object}
        anchorRef={cardRef}
        open={previewOpen}
        disabled={!showHoverCard}
        ownerUsername={ownerUsername}
      />
    </div>
  );
}
